import {
  MoneyBags,
  Play,
  TagFavorite,
  Profile,
  Stats,
  ShoppingBasket,
  Cutlery,
  Document,
  BuildingRestaurant,
  CaretUp,
  CaretDown,
  WoltToken,
} from '@creditornot/cb-icons';
import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { LegacyPopover } from '@creditornot/cb-popover';
import { useDirection } from '@creditornot/cb-provider';
import { Tag } from '@creditornot/cb-tag';
import React, { ReactElement, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { NavLink, matchPath } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { CashPayment, MapsPin, Market, Translate } from 'common/components/icons';
import MenuItem from 'common/components/icons/MenuItem';
import WoltStar from 'common/components/icons/WoltStar';
import { useIsMobile } from 'common/hooks/useIsMobile';
import { Typography } from 'common/typography';

import Tooltip from './Tooltip';
import { reportOpenNavigationItem } from './behavioral';
import { AvailableRouteItem, NavigationItem, TooltipItem } from './types';
import { CoinStackIcon } from '../menu/bulk-actions/components/BulkPriceChange';

type CollapsibleSidebarItemsProps = {
  item: AvailableRouteItem;
  tooltip?: TooltipItem;
  isCollapsed: boolean;
  activeTabId?: string;
  setIsCollapsed: (isCollapsed: boolean) => void;
};

const IconsMapping: { [key in NavigationItem]?: ReactElement } = {
  ACCESS_MANAGEMENT: <Profile />,
  BEST_OF_WOLT: <WoltStar />,
  BRAND_INSIGHTS: <Stats />,
  BRAND_REPORTING: <Document />,
  ENTERPRISE_INSIGHTS: <Stats />,
  ENTERPRISE_REPORTING: <Document />,
  INSIGHTS: <Stats />,
  INSIGHTS_V2: <Stats />,
  LIVE_PURCHASES: <Play />,
  MENU_EDITOR_RESTAURANT: <Cutlery />,
  MENU_EDITOR_RETAIL: <ShoppingBasket />,
  PAYOUT_REPORTS: <CoinStackIcon />,
  PICKING_ROUTE: <MapsPin />,
  PROMOTIONS: <TagFavorite />,
  PURCHASES: <MoneyBags />,
  SETTLEMENTS: <CashPayment />,
  SHARED_MENU: <Document />,
  TRANSLATION: <Translate />,
  VENUE_DETAILS: <Market />,
  CAMPAIGNS: <MenuItem />,
  BRAND_VENUE_GROUPS: <BuildingRestaurant />,
  VENUES: <BuildingRestaurant />,
  REPORTS: <CoinStackIcon />,
  WOLT_CAPITAL: <WoltToken />,
};

const baseItemStyle = css`
  display: flex;
  align-items: center;
  color: ${cbSemanticCssVars.textSecondary};
  fill: ${cbSemanticCssVars.textSecondary};
  flex-shrink: 0;
  cursor: pointer;
  border-radius: 4px;
  gap: 4px;

  &&:hover {
    background-color: ${cbSemanticCssVars.surfaceMain};
  }

  &.active {
    color: ${colors.wolt100};
    fill: ${colors.wolt100};
  }
`;

const CollapsibleSidebarMainItemContainer = styled.div<{ isCollapsed: boolean }>`
  ${baseItemStyle}
  width: ${({ isCollapsed }) => (isCollapsed ? '44px' : '100%')};
  &.active {
    background-color: ${colors.wolt8};
  }
`;

const CollapsibleSidebarMainItem = styled(NavLink)`
  display: flex;
  align-items: center;
  color: ${cbSemanticCssVars.textSecondary};
  fill: ${cbSemanticCssVars.textSecondary};
  flex-grow: 1;

  &.selected {
    color: ${colors.wolt100};
    fill: ${colors.wolt100};
  }
`;

const CollapsibleSidebarSubItem = styled(NavLink)`
  ${baseItemStyle}
  padding-block: 8px;
  padding-inline-start: 12px;
  margin-inline-start: 32px;

  &.active {
    background-color: ${colors.wolt8};
  }
`;

const CollapsibleSidebarItemLabel = styled.div<{ isCollapsed: boolean }>`
  display: ${({ isCollapsed }) => (isCollapsed ? 'none' : 'flex')};
  flex-grow: 1;
  justify-content: flex-start;
  opacity: ${({ isCollapsed }) => (isCollapsed ? 0 : 1)};
  transition: opacity 1s ease-out;
  transition: display 1s ease-in;
  align-items: center;
  gap: 8px;
`;

const CollapsibleSidebarItemLabelText = styled(Typography.Body3)<{ hasTag: boolean; isMobile: boolean }>`
  max-width: ${({ hasTag, isMobile }) => (hasTag && !isMobile ? '120px' : '100%')};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CollapsibleSidebarItemIconContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding: 12px;

  width: 44px;
  height: 44px;

  border-radius: 4px;

  transition: all 0.4s ease-in-out;

  &.active {
    fill: ${colors.wolt100};
  }
  &.selected {
    fill: ${colors.wolt100};
  }
`;

const ItemPopover = styled(LegacyPopover)`
  width: 100%;
`;

const StyledCaretUp = styled(CaretUp)`
  padding: 2px;
  width: 16px;
  margin-inline-end: 8px;
`;

const StyledCaretDown = styled(CaretDown)`
  padding: 2px;
  width: 16px;
  margin-inline-end: 8px;
`;

const StyledTag = styled(Tag)`
  padding: 4px;
  margin-inline-end: 2px;
`;

const NEW_FEATURES: NavigationItem[] = ['CAMPAIGNS', 'BEST_OF_WOLT', 'INSIGHTS_V2_RATINGS_AND_REVIEWS', 'WOLT_CAPITAL'];

const CollapsibleSidebarItem = ({
  item,
  tooltip,
  isCollapsed,
  activeTabId,
  setIsCollapsed,
}: CollapsibleSidebarItemsProps) => {
  const isMobile = useIsMobile();
  const tooltipPlacement = useDirection() === 'ltr' ? 'right' : 'left';

  const subActiveTabId = item?.subItems?.find(route =>
    matchPath(location.pathname, { path: route.link, exact: route.exact }),
  )?.link;
  const [isSubItemsOpen, setIsSubItemsOpen] = React.useState(false);

  useEffect(() => {
    setIsSubItemsOpen(activeTabId === item.link);
  }, [activeTabId, setIsSubItemsOpen, item.link]);

  const handleClickMenuItem = (item: AvailableRouteItem) => {
    item.trackingKey && reportOpenNavigationItem(item.trackingKey);
    isMobile && setIsCollapsed(true);
  };

  const toggleSubItems = () => {
    setIsSubItemsOpen(!isSubItemsOpen);
  };

  const showNewTag = NEW_FEATURES.includes(item.type) && (isMobile ? true : !tooltip);

  const isShowExternalTooltip =
    tooltip?.id === item.type ||
    ((isCollapsed || !isSubItemsOpen) && item.subItems?.some(subItem => tooltip?.id === subItem.type));

  return (
    <>
      <ItemPopover
        content={
          isShowExternalTooltip && !isCollapsed && tooltip ? (
            <Tooltip text={tooltip.text} dismiss={tooltip.dismiss} tag={tooltip.tag} />
          ) : (
            <Typography.Body3>{item.label}</Typography.Body3>
          )
        }
        disabled={isShowExternalTooltip ? isMobile : !isCollapsed || isMobile}
        placement={tooltipPlacement}
        showOnClick={false}
        show={isShowExternalTooltip && !isCollapsed ? true : undefined}
        key={item.link}
      >
        <CollapsibleSidebarMainItemContainer
          isCollapsed={isCollapsed}
          // If sub-item is selected, don't show the active style on the main item
          className={!subActiveTabId && activeTabId === item.link ? 'selected' : ''}
        >
          <CollapsibleSidebarMainItem
            exact={item.exact}
            to={item.link}
            onClick={() => handleClickMenuItem(item)}
            className={!subActiveTabId && activeTabId === item.link ? 'selected' : ''}
          >
            <CollapsibleSidebarItemIconContainer className={activeTabId === item.link ? 'selected' : ''}>
              {IconsMapping[item.type]}
            </CollapsibleSidebarItemIconContainer>
            <CollapsibleSidebarItemLabel isCollapsed={isCollapsed}>
              <CollapsibleSidebarItemLabelText hasTag={showNewTag} isMobile={isMobile}>
                {item.label}
              </CollapsibleSidebarItemLabelText>
              {showNewTag && (
                <StyledTag variant="blue" size="tiny">
                  <FormattedMessage id="navigation.new" />
                </StyledTag>
              )}
            </CollapsibleSidebarItemLabel>
          </CollapsibleSidebarMainItem>
          {item?.subItems?.length !== undefined && item?.subItems?.length > 0 && (
            <>
              {isSubItemsOpen ? (
                <StyledCaretUp onClick={toggleSubItems} />
              ) : (
                <StyledCaretDown onClick={toggleSubItems} />
              )}
            </>
          )}
        </CollapsibleSidebarMainItemContainer>
      </ItemPopover>

      {!isCollapsed &&
        isSubItemsOpen &&
        item.subItems?.map(subItem => {
          return (
            <ItemPopover
              content={
                tooltip?.id === subItem.type ? (
                  <Tooltip text={tooltip.text} dismiss={tooltip.dismiss} tag={tooltip.tag} />
                ) : null
              }
              disabled={tooltip?.id !== subItem.type || isMobile}
              placement={tooltipPlacement}
              showOnClick={false}
              show={tooltip?.id === subItem.type && !isCollapsed ? true : undefined}
              key={subItem.link}
            >
              <CollapsibleSidebarSubItem
                key={subItem.link}
                className={subActiveTabId === subItem.link ? 'active' : ''}
                exact={subItem.exact}
                to={subItem.link}
                onClick={() => handleClickMenuItem(item)}
              >
                <CollapsibleSidebarItemLabel isCollapsed={isCollapsed}>
                  <Typography.Body3>{subItem.label}</Typography.Body3>
                </CollapsibleSidebarItemLabel>
              </CollapsibleSidebarSubItem>
            </ItemPopover>
          );
        })}
    </>
  );
};

export default CollapsibleSidebarItem;
